/**
 * API Service
 * 
 * Centralized service for communicating with the Enterprise Search backend.
 */

import { config } from '../config';
import type {
  SearchRequest,
  SearchResponse,
  HealthResponse,
  FacetsResponse,
  SuggestRequest,
  SuggestResponse,
  DocumentResponse,
} from '../types/search';
import type {
  FeedbackConfig,
  FeedbackResponse,
  FeedbackSubmission,
} from '../types/feedback';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface ApiError {
  message: string;
  status?: number;
  detail?: string;
}

export class ApiServiceError extends Error {
  status?: number;
  detail?: string;

  constructor(message: string, status?: number, detail?: string) {
    super(message);
    this.name = 'ApiServiceError';
    this.status = status;
    this.detail = detail;
  }
}

// -----------------------------------------------------------------------------
// API Service
// -----------------------------------------------------------------------------

class ApiService {
  private baseUrl: string;
  private accessToken: string | null = null;

  constructor() {
    this.baseUrl = config.api.baseUrl;
  }

  /**
   * Set the access token for authenticated requests
   */
  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  /**
   * Make an authenticated fetch request
   */
  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // FormData bodies must keep the browser-generated multipart boundary, so
    // the JSON content type is only applied to non-FormData requests.
    const isFormData = options.body instanceof FormData;
    const headers: HeadersInit = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    };

    // Add authorization header if we have a token
    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        let detail: string | undefined;
        try {
          const errorBody = await response.json();
          detail = errorBody.detail || errorBody.message;
        } catch {
          // Could not parse error body
        }
        
        throw new ApiServiceError(
          `API request failed: ${response.statusText}`,
          response.status,
          detail
        );
      }

      return response.json();
    } catch (error) {
      if (error instanceof ApiServiceError) {
        throw error;
      }
      
      // Network or other error
      throw new ApiServiceError(
        error instanceof Error ? error.message : 'Network error',
        undefined,
        'Could not connect to the server'
      );
    }
  }
  // ---------------------------------------------------------------------------
  // Health
  // ---------------------------------------------------------------------------

  /**
   * Check API health status
   */
  async checkHealth(): Promise<HealthResponse> {
    return this.fetch<HealthResponse>('/api/v1/health');
  }

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------

  /**
   * Perform a search query
   */
  async search(request: SearchRequest): Promise<SearchResponse> {
    return this.fetch<SearchResponse>('/api/v1/search', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // ---------------------------------------------------------------------------
  // Documents
  // ---------------------------------------------------------------------------

  /**
   * Get a single document by ID. Rejects with status 404 when the document
   * does not exist or the current user may not see it.
   */
  async getDocument(documentId: string): Promise<DocumentResponse> {
    return this.fetch<DocumentResponse>(
      `/api/v1/documents/${encodeURIComponent(documentId)}`
    );
  }

  // ---------------------------------------------------------------------------
  // Facets
  // ---------------------------------------------------------------------------

  /**
   * Get available filter facets with metadata
   * Returns dynamic facet values from the backend with display names and counts.
   *
   * Pass an `audience` (e.g. `"search_frontend"`) to scope the response:
   * facet values tagged for other audiences are excluded, and fields whose
   * backend metadata hides them from this audience (e.g. contributor type)
   * are omitted entirely.
   */
  async getFacets(audience?: string): Promise<FacetsResponse> {
    const path = audience
      ? `/api/v1/facets?audience=${encodeURIComponent(audience)}`
      : '/api/v1/facets';
    return this.fetch<FacetsResponse>(path);
  }

  // ---------------------------------------------------------------------------
  // Suggest (type-ahead)
  // ---------------------------------------------------------------------------

  /**
   * Get type-ahead suggestions for a prefix query.
   *
   * Supports request cancellation via `signal` — pass an AbortController.signal
   * to cancel in-flight requests when the user keeps typing. AbortErrors are
   * re-thrown unchanged so callers can detect them and ignore silently.
   */
  async suggest(
    request: SuggestRequest,
    signal?: AbortSignal
  ): Promise<SuggestResponse> {
    const params = new URLSearchParams();
    params.set('q', request.q);
    if (request.size !== undefined) params.set('size', String(request.size));
    if (request.user_language) params.set('user_language', request.user_language);
    if (request.audience) params.set('audience', request.audience);

    const url = `${this.baseUrl}/api/v1/suggest?${params.toString()}`;
    const headers: HeadersInit = {};
    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response: Response;
    try {
      response = await fetch(url, { headers, signal });
    } catch (err) {
      // Re-throw AbortError verbatim so callers can detect cancellation.
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      throw new ApiServiceError(
        err instanceof Error ? err.message : 'Network error',
        undefined,
        'Could not connect to the server'
      );
    }

    if (!response.ok) {
      let detail: string | undefined;
      try {
        const body = await response.json();
        detail = body.detail || body.message;
      } catch {
        /* ignore */
      }
      throw new ApiServiceError(
        `Suggest request failed: ${response.statusText}`,
        response.status,
        detail
      );
    }

    return response.json();
  }

  // ---------------------------------------------------------------------------
  // Feedback
  // ---------------------------------------------------------------------------

  /**
   * Get the feedback widget configuration (enabled flag + upload limits).
   */
  async getFeedbackConfig(): Promise<FeedbackConfig> {
    return this.fetch<FeedbackConfig>('/api/v1/feedback/config');
  }

  /**
   * Submit in-app feedback. Creates a support ticket on the backend.
   *
   * Sent as multipart/form-data so attachments can be streamed along with the
   * text fields. Works for anonymous users; the bearer token is attached when
   * one is available.
   */
  async submitFeedback(submission: FeedbackSubmission): Promise<FeedbackResponse> {
    const form = new FormData();
    form.append('category', submission.category);
    form.append('summary', submission.summary);
    form.append('details', submission.details);
    if (submission.pageUrl) form.append('page_url', submission.pageUrl);
    if (submission.searchQuery) form.append('search_query', submission.searchQuery);
    if (submission.uiLanguage) form.append('ui_language', submission.uiLanguage);
    if (submission.email) form.append('contact_email', submission.email);
    for (const file of submission.files) {
      form.append('files', file, file.name);
    }

    return this.fetch<FeedbackResponse>('/api/v1/feedback', {
      method: 'POST',
      body: form,
    });
  }
}

// Export a singleton instance
export const apiService = new ApiService();

export default apiService;
